import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material';
import cx from 'classnames';
import ErrorMessage from '../ErrorMessage';
import DialogBaseWrapper from './dialogWrappers/DialogBaseWrapper';
import DialogFormWrapper from './dialogWrappers/DialogFormWrapper';
import { DIALOG_MODES } from './dialogWrappers/constants/DialogModes';

/**
 * @typedef {object} DialogSettings
 * @property {'button' | 'submit' } submitButton
 * @property {React.ElementType} wrapper
 * @property {(handleOnConfirm?: (data?: Object) => void | Promise<void>) => ((event: React.MouseEvent) => void) | undefined} submitFn
 */

/**
 * @typedef {object} DialogModeConfig
 * @property {number} mode
 * @property {DialogSettings} settings
 */

/** @type {DialogModeConfig[]} */
const DIALOG_SETTINGS = [
  {
    mode: DIALOG_MODES.BASE,
    settings: {
      submitButton: 'button',
      wrapper: DialogBaseWrapper,
      submitFn: handleOnConfirm => handleOnConfirm
    }
  },
  {
    mode: DIALOG_MODES.INTERNAL_FORM,
    settings: {
      submitButton: 'submit',
      wrapper: DialogFormWrapper,
      submitFn: () => undefined
    }
  },
  {
    mode: DIALOG_MODES.EXTERNAL_FORM_SUBMIT,
    settings: {
      submitButton: 'submit',
      wrapper: DialogBaseWrapper,
      submitFn: () => undefined
    }
  }
];

/**
 * @typedef {object} ConfirmDialogProps
 * @property {boolean} [open]
 * @property {(event: object, reason?: 'backdropClick' | 'escapeKeyDown') => void} [handleClose]
 * @property {(data?: Object) => void | Promise<void>} [handleOnConfirm]
 * @property {string} [message]
 * @property {string} [confirmBtnText]
 * @property {string} [cancelBtnText]
 * @property {string} [dialogClassName]
 * @property {boolean} [disableConfirmBtn]
 * @property {React.ReactElement} [dialogContent]
 * @property {string} [errorMessage]
 * @property {number} [dialogMode]
 * @property {string} [formId]
 */

/**
 * @param {ConfirmDialogProps} props
 */
const ConfirmDialog = props => {
  const {
    open,
    message,
    dialogContent,
    dialogClassName = '',
    confirmBtnText = 'Remove',
    cancelBtnText = 'Cancel',
    disableConfirmBtn,
    errorMessage = '',
    handleClose,
    handleOnConfirm,
    dialogMode = DIALOG_MODES.BASE,
    formId
  } = props;

  const dialogSettings = DIALOG_SETTINGS[dialogMode].settings;
  const DialogWrapperInternal = dialogSettings.wrapper;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={cx('dialog', dialogClassName)}
      fullWidth
      maxWidth='md'
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'>
      <DialogWrapperInternal handleConfirm={handleOnConfirm}>
        <DialogContent>
          {message && <DialogContentText>{message}</DialogContentText>}
          {dialogContent}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} className='secondary-button sm' type='button'>
            {cancelBtnText}
          </Button>
          <Button
            className='primary-button sm'
            disabled={disableConfirmBtn}
            onClick={dialogSettings.submitFn(handleOnConfirm)}
            type={dialogSettings.submitButton}
            form={formId}>
            {confirmBtnText}
          </Button>
        </DialogActions>
        {errorMessage && <ErrorMessage message={errorMessage} />}
      </DialogWrapperInternal>
    </Dialog>
  );
};

export default ConfirmDialog;
